import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaGlobe, FaCheck, FaTimes } from "react-icons/fa";
import Navbar from "../components/Navbar";
import { supabase } from "../utils/supabaseClient";
import LogoPiaWak from "../utils/piawak.png";

import translations from "../utils/registertransalte";

export default function OperatorRegister() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState("es");
  const [pictureFile, setPictureFile] = useState(null);

  // Traducciones
  const t = useMemo(() => translations[language], [language]);
  const URL = process.env.REACT_APP_SERVER_URL;

  const [formData, setFormData] = useState({
    name: "",
    last_name1: "",
    last_name2: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    location: "",
    picture: "",
    language_operator: language,
    description: "",
    name_submerchant: ""
  });

  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    last_name1: "",
    last_name2: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "", 
    name_submerchant: ""
  });

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
    noSpaces: true,
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  // --- Validaciones ---
  const validateNameField = (fieldName, value) => {
    const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ]+(?: [a-zA-ZáéíóúÁÉÍÓÚñÑ]+)*$/;

    if (!value?.trim()) return t.fieldRequired;
    if (value.trim().length < 2) return t.minLength;

    if (!nameRegex.test(value)) {
      if (/\d/.test(value)) return t.noNumbers;
      if (/\s{2,}/.test(value)) return t.noMultipleSpaces;
      if (/^\s|\s$/.test(value)) return t.noEdgeSpaces;
      return fieldName === "name" ? t.nameError : t.lastNameError;
    }
    return "";
  };

  const validateEmail = (email) => {
    return !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      ? `${t.email} ${t.fieldRequired.toLowerCase()}`
      : "";
  };

  const validatePassword = (password) => {
    return password.length < 8 ? t.reqLength : "";
  };

  const validateConfirmPassword = (password, confirmPassword) => {
    return confirmPassword && password !== confirmPassword ? t.passwordError : "";
  };

  // ✅ Validación de teléfono CR (opcional; solo si el usuario escribe algo)
  const validatePhone = (phone) => {
    if (!phone || !phone.trim()) return ""; // No requerido

    // Permitir espacios, guiones, paréntesis y puntos
    const cleaned = phone.replace(/[ \-().]/g, "");
    // Si incluye +, debe ser +506
    if (cleaned.startsWith("+") && !cleaned.startsWith("+506")) {
      return t.phoneErrorPrefix;
    }

    // Quitar +506 si está presente
    const local = cleaned.startsWith("+506") ? cleaned.slice(4) : cleaned;

    // Debe ser 8 dígitos iniciando en 2–8 (fijo: 2/4; móvil: 6/7/8)
    const crPattern = /^[2-8][0-9]{7}$/;

    if (!crPattern.test(local)) {
      return t.phoneErrorInvalid;
    }
    return "";
  };
const validate_name_submerchant = (value) => {
  if (!value || value.trim() === "") {
    return t.name_submerchant_error;
  }
  return ""; // ✅ importante devolver cadena vacía cuando no hay error
};

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (["name", "last_name1", "last_name2"].includes(name)) {
      setFieldErrors((prev) => ({ ...prev, [name]: validateNameField(name, value) }));
    } else if (name === "email") {
      setFieldErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else if (name === "password") {
      setFieldErrors((prev) => ({ ...prev, password: validatePassword(value) }));
    } else if (name === "confirmPassword") {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(formData.password, value),
      }));
    } else if (name === "phone") {
      setFieldErrors((prev) => ({ ...prev, phone: validatePhone(value) }));
    } else if (name === "name_submerchant") {
      setFieldErrors((prev) => ({ ...prev, name_submerchant:validate_name_submerchant(value)}));
    }
  };

  // Validación de contraseña en tiempo real
  useEffect(() => {
    if (formData.password) {
      setPasswordRequirements({
        length: formData.password.length >= 8,
        uppercase: /[A-Z]/.test(formData.password),
        lowercase: /[a-z]/.test(formData.password),
        number: /[0-9]/.test(formData.password),
        specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
        noSpaces: !/\s/.test(formData.password),
      });

      setFieldErrors((prev) => ({
        ...prev,
        password: validatePassword(formData.password),
      }));
    } else {
      setPasswordRequirements({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
        noSpaces: true,
      });
    }
  }, [formData.password]);

  // Validar si se puede enviar el formulario
  useEffect(() => {
    const passwordValid = Object.values(passwordRequirements).every(Boolean);
    const mismatch =
      formData.password &&
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword;

    const noFieldErrors = Object.values(fieldErrors).every((error) => error === "");
    const requiredFieldsFilled =
      formData.name &&
      formData.last_name1 &&
      formData.last_name2 &&
      formData.email &&
      formData.password &&
      formData.confirmPassword && 
      formData.name_submerchant;

    setPasswordMismatch(mismatch);
    setCanSubmit(!mismatch && passwordValid && noFieldErrors && requiredFieldsFilled);
  }, [formData, passwordRequirements, fieldErrors]);

  // Traducción de errores genéricos
  const translateErrorMessage = useMemo(
    () => (message) => {
      const errorTranslations = {
        "Error al subir la imagen.": t.uploadError,
        "Ocurrió un error.": t.genericError,
        "Error uploading image.": t.uploadError,
        "An error occurred.": t.genericError,
        [t.uploadError]: t.uploadError,
        [t.genericError]: t.genericError,
      };
      return errorTranslations[message] || message;
    },
    [t]
  );

  // Recalcular errores al cambiar idioma
  useEffect(() => {
    if (Object.values(fieldErrors).some((e) => e !== "")) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        let needsUpdate = false;

        Object.keys(newErrors).forEach((key) => {
          if (newErrors[key]) {
            let newError = "";
            if (["name", "last_name1", "last_name2"].includes(key)) {
              newError = validateNameField(key, formData[key]);
            } else if (key === "email") {
              newError = validateEmail(formData.email);
            } else if (key === "password") {
              newError = validatePassword(formData.password);
            } else if (key === "confirmPassword") {
              newError = validateConfirmPassword(formData.password, formData.confirmPassword);
            } else if (key === "phone") {
              newError = validatePhone(formData.phone);
            }
            if (newError !== newErrors[key]) {
              newErrors[key] = newError;
              needsUpdate = true;
            }
          }
        });
        return needsUpdate ? newErrors : prev;
      });
    }

    if (errorMessage) {
      const translatedError = translateErrorMessage(errorMessage);
      if (translatedError !== errorMessage) {
        setErrorMessage(translatedError);
      }
    }
  }, [language]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setPictureFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    let picturePath = formData.picture;

    if (pictureFile) {
      const fileExt = pictureFile.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `profile/${fileName}`;

      const { error } = await supabase.storage
        .from("operator-pictures")
        .upload(filePath, pictureFile, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error.message);
        setErrorMessage(t.uploadError);
        return;
      }
      picturePath = filePath;
    }

    try {
      const response = await fetch(`${URL}/api/authOperator/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          picture: picturePath,
          language_operator: language,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        navigate("/emailValidate");
      } else {
        setErrorMessage(result.errors || t.genericError);
      }
    } catch (error) {
      console.error("Network error:", error);
      setErrorMessage(t.serverError);
    }
  };

  const inputClass = (fieldName) =>
    `w-full p-3 rounded-xl bg-white border ${
      fieldErrors[fieldName] ? "border-red-500" : "border-[#DAE2E0]"
    } focus:outline-none focus:ring-2 ${
      fieldErrors[fieldName] ? "focus:ring-red-400" : "focus:ring-[#2A9D8F]"
    } placeholder-[#264653] placeholder-opacity-60 text-sm transition duration-200 text-[#264653]`;

  const passwordStrengthClass = `w-full h-1 mt-1 rounded-full ${
    formData.password.length === 0
      ? "bg-[#DAE2E0]"
      : formData.password.length < 4
      ? "bg-red-500"
      : formData.password.length < 8
      ? "bg-[#E9C46A]"
      : Object.values(passwordRequirements).filter(Boolean).length < 5
      ? "bg-[#F4A261]"
      : "bg-[#2A9D8F]"
  }`;

  return (
    <>
      <Navbar />
      <div className="min-h-screen  via-[#89B98A] to-[#E9C46A] bg-opacity-5 pt-14 flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-10 xl:p-12">
        <div className="bg-[#FCFCF9] text-[#264653] w-full max-w-5xl rounded-3xl shadow-xl overflow-hidden animate-fade-in mt-3 border border-[#DAE2E0]">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left */}
            <div className="relative bg-gradient-to-br from-[#2A9D8F] to-[#E9C46A] p-6 sm:p-8 md:p-10 flex flex-col justify-center items-center text-white overflow-hidden">
              {/* Elementos decorativos orgánicos */}
              <div className="absolute top-4 right-4 w-20 h-20 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-20"></div>
              <div className="absolute bottom-4 left-4 w-16 h-16 bg-[#F4A261] rounded-full mix-blend-soft-light opacity-25"></div>
              <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-30"></div>
              
              <img
                src={LogoPiaWak}
                alt="Pia Wak Logo"
                className="w-34 h-32 sm:w-40 sm:h-40 object-contain mb-4 sm:mb-6 animate-fade-in relative z-10"
              />
              <h2 className="text-xl sm:text-2xl font-bold text-center relative z-10">{t.welcome}</h2>
              <p className="text-center mt-2 text-xs sm:text-sm text-[#FCFCF9] text-opacity-90 relative z-10">{t.subtitle}</p>
              <div className="mt-2 sm:mt-4 text-center text-xs sm:text-sm text-[#FCFCF9] text-opacity-80 italic relative z-10">
                {t.motto}
              </div>

              <button
                onClick={() => setLanguage(language === "es" ? "en" : "es")}
                className="absolute top-2 sm:top-4 right-2 sm:right-4 flex items-center gap-1 sm:gap-2 bg-[#FCFCF9] text-[#2A9D8F] px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-lg shadow hover:bg-opacity-90 transition relative z-20"
              >
                <FaGlobe className="text-xs sm:text-sm" />
                {t.switchLang}
              </button>
            </div>

            {/* Right */}
            <div className="p-6 sm:p-8 md:p-10 bg-[#FCFCF9]">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-center text-[#264653]">
                {t.registerTitle}
              </h2>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 sm:gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t.name}
                      className={inputClass("name")}
                      required
                    />
                    {fieldErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      name="last_name1"
                      value={formData.last_name1}
                      onChange={handleChange}
                      placeholder={t.lastName1}
                      className={inputClass("last_name1")}
                      required
                    />
                    {fieldErrors.last_name1 && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.last_name1}</p>
                    )}
                  </div>
                </div>

                <div>
                  <input
                    type="text"
                    name="last_name2"
                    value={formData.last_name2}
                    onChange={handleChange}
                    placeholder={t.lastName2}
                    className={inputClass("last_name2")}
                    required
                  />
                  {fieldErrors.last_name2 && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.last_name2}</p>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.email}
                    className={inputClass("email")}
                    required
                  />
                  {fieldErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                  )}
                </div>

                <div className="mb-1 sm:mb-2">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t.password}
                    className={inputClass("password")}
                    required
                  />
                  <div className={passwordStrengthClass}></div>
                  <div className="mt-1 sm:mt-2">
                    <p className="text-xs text-[#264653] text-opacity-70 mb-1">{t.passwordRequirements}</p>
                    <ul className="text-xs space-y-0.5 sm:space-y-1">
                      <li className={`flex items-center ${passwordRequirements.length ? "text-[#2A9D8F]" : "text-[#264653] text-opacity-50"}`}>
                        {passwordRequirements.length ? <FaCheck className="mr-1 text-xs" /> : <FaTimes className="mr-1 text-xs" />}
                        {t.reqLength}
                      </li>
                      <li className={`flex items-center ${passwordRequirements.uppercase ? "text-[#2A9D8F]" : "text-[#264653] text-opacity-50"}`}>
                        {passwordRequirements.uppercase ? <FaCheck className="mr-1 text-xs" /> : <FaTimes className="mr-1 text-xs" />}
                        {t.reqUppercase}
                      </li>
                      <li className={`flex items-center ${passwordRequirements.lowercase ? "text-[#2A9D8F]" : "text-[#264653] text-opacity-50"}`}>
                        {passwordRequirements.lowercase ? <FaCheck className="mr-1 text-xs" /> : <FaTimes className="mr-1 text-xs" />}
                        {t.reqLowercase}
                      </li>
                      <li className={`flex items-center ${passwordRequirements.number ? "text-[#2A9D8F]" : "text-[#264653] text-opacity-50"}`}>
                        {passwordRequirements.number ? <FaCheck className="mr-1 text-xs" /> : <FaTimes className="mr-1 text-xs" />}
                        {t.reqNumber}
                      </li>
                      <li className={`flex items-center ${passwordRequirements.specialChar ? "text-[#2A9D8F]" : "text-[#264653] text-opacity-50"}`}>
                        {passwordRequirements.specialChar ? <FaCheck className="mr-1 text-xs" /> : <FaTimes className="mr-1 text-xs" />}
                        {t.reqSpecialChar}
                      </li>
                      <li className={`flex items-center ${passwordRequirements.noSpaces ? "text-[#2A9D8F]" : "text-[#264653] text-opacity-50"}`}>
                        {passwordRequirements.noSpaces ? <FaCheck className="mr-1 text-xs" /> : <FaTimes className="mr-1 text-xs" />}
                        {t.reqNoSpaces}
                      </li>
                    </ul>
                  </div>
                </div>

                <div>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder={t.confirmPassword}
                    className={
                      fieldErrors.confirmPassword
                        ? inputClass("confirmPassword") + " border-red-500"
                        : inputClass("confirmPassword")
                    }
                    required
                  />
                  {fieldErrors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{t.passwordError}</p>
                  )}
                </div>
                 <div>
                  <input
                    type="text"
                    name="name_submerchant"
                    value={formData.name_submerchant}
                    onChange={handleChange}
                    placeholder={t.name_submerchant}
                    className={inputClass("last_name2")}
                    required
                  />
                  {fieldErrors.name_submerchant && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.name_submerchant}</p>
                  )}
                </div>

                
                <div>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t.phone}
                    className={inputClass("phone")}
                  />
                  {fieldErrors.phone && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>
                  )}
                </div>

                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder={t.location}
                  className={inputClass("location")}
                />

                <div>
                  <label className="text-xs sm:text-sm text-[#264653] text-opacity-70 mb-1 block">
                    {t.pictureLabel}
                  </label>
                  <input
                    type="text"
                    name="picture"
                    value={formData.picture}
                    onChange={handleChange}
                    placeholder="https://..."
                    className={`${inputClass("picture")} mb-1 sm:mb-2`}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full text-xs sm:text-sm text-[#264653] text-opacity-70 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-xl file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-[#2A9D8F] file:bg-opacity-10 file:text-[#2A9D8F] hover:file:bg-opacity-20 transition"
                  />
                </div>

                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={t.description}
                  rows={4}
                  className={`${inputClass("description")} resize-none`}
                />

                {errorMessage && (
                  <div className="text-red-600 bg-red-100 border border-red-300 p-2 rounded-lg text-xs sm:text-sm">
                    {typeof errorMessage === "string" ? (
                      <p className="text-center">{errorMessage}</p>
                    ) : (
                      Object.entries(errorMessage).map(([field, message]) => (
                        <p key={field}>
                          <strong>{field}:</strong> {message}
                        </p>
                      ))
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full mt-1 sm:mt-2 font-semibold py-2 sm:py-3 rounded-xl shadow transition-all duration-200 ${
                    canSubmit
                      ? "bg-gradient-to-r from-[#2A9D8F] to-[#45B69C] text-white hover:from-[#21867a] hover:to-[#3aa38c] transform hover:scale-105"
                      : "bg-[#DAE2E0] text-[#264653] text-opacity-50 cursor-not-allowed"
                  }`}
                >
                  {t.register}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}